import { Pill, useTheme } from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';
import {
  Background,
  BaseEdge,
  Controls,
  Edge,
  EdgeProps,
  getStraightPath,
  Handle,
  InternalNode,
  MiniMap,
  Node,
  NodeProps,
  Position,
  ReactFlow,
  useInternalNode,
  XYPosition,
} from '@xyflow/react';
import classnames from 'classnames';
import React, { useMemo } from 'react';

import { Routes } from '../../routes';

import styles from './company_group_graph.module.css';

export type CompanyGroupCompany = {
  id: string;
  name: string | null;
  isTest: boolean;
  isHub: boolean;
  isMember: boolean;
  isCurrent: boolean;
};

export interface CompanyGroupLink {
  id: string;
  hubId: string;
  memberId: string;
  label: string;
}

export interface CompanyGroupPartner {
  memberId: string;
  partnerId: string;
}

interface CompanyGroupGraphProps {
  companies: CompanyGroupCompany[];
  links: CompanyGroupLink[];
  partners: CompanyGroupPartner[];
}

type CompanyNode = Node<CompanyGroupCompany & { subcontractorCount: number }, 'company'>;
type MembershipEdge = Edge<{ label: string }, 'membership'>;

const NODE_WIDTH = 240;
const NODE_HEIGHT = 56;
const MIN_RING_RADIUS = 260;
const RING_NODE_SPACING = 300;
const HUB_GAP = 240;

const nodeTypes = { company: CompanyNodeView };
const edgeTypes = { membership: FloatingEdge };

export const CompanyGroupGraph: React.FC<CompanyGroupGraphProps> = ({ companies, links, partners }) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.includes('dark') ?? false;

  const { nodes, edges } = useMemo(() => {
    const positions = layoutCompanies(companies, links);
    return {
      nodes: companies.map((company): CompanyNode => ({
        id: company.id,
        type: 'company',
        position: positions.get(company.id) ?? { x: 0, y: 0 },
        data: { ...company, subcontractorCount: countSubcontractors(partners, company.id) },
      })),
      edges: links.map((link): MembershipEdge => ({
        id: link.id,
        type: 'membership',
        source: link.hubId,
        target: link.memberId,
        data: { label: link.label },
      })),
    };
  }, [companies, links, partners]);

  return (
    <div className={styles.viewport}>
      <ReactFlow
        defaultNodes={nodes}
        defaultEdges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        colorMode={isDark ? 'dark' : 'light'}
        nodesConnectable={false}
        deleteKeyCode={null}
        minZoom={0.1}
        fitView
        proOptions={{ hideAttribution: true }}>
        <Background />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable />
      </ReactFlow>
    </div>
  );
};

function CompanyNodeView({ data }: NodeProps<CompanyNode>) {
  return (
    <div
      className={classnames(styles.node, {
        [styles.node_hub]: data.isHub,
        [styles.node_current]: data.isCurrent,
      })}>
      <Handle type="target" position={Position.Top} className={styles.handle} isConnectable={false} />
      <a
        href={Routes.CompanyRelationships.generatePath({ company_id: data.id })}
        target="_blank"
        rel="noopener noreferrer"
        title={data.name ?? data.id}
        className={classnames('nodrag', styles.node__name)}>
        {data.name ?? 'Unnamed company'}
      </a>
      <span className={styles.node__meta}>
        <span>#{data.id}</span>
        <span>· {describeRole(data)}</span>
        {data.isTest && <Pill kind="warning">Test</Pill>}
      </span>
      <span className={styles.node__meta}>{pluralize(data.subcontractorCount, 'subcontractor')}</span>
      <Handle type="source" position={Position.Bottom} className={styles.handle} isConnectable={false} />
    </div>
  );
}

function FloatingEdge({ id, source, target, data }: EdgeProps<MembershipEdge>) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);

  if (isNil(sourceNode) || isNil(targetNode)) {
    return null;
  }

  const start = getBorderIntersection(sourceNode, targetNode);
  const end = getBorderIntersection(targetNode, sourceNode);
  const [path] = getStraightPath({ sourceX: start.x, sourceY: start.y, targetX: end.x, targetY: end.y });

  return (
    <g>
      <title>{data?.label}</title>
      <BaseEdge id={id} path={path} />
    </g>
  );
}

function countSubcontractors(partners: CompanyGroupPartner[], companyId: string): number {
  return new Set(partners.filter(({ memberId }) => memberId === companyId).map(({ partnerId }) => partnerId)).size;
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function describeRole(company: CompanyGroupCompany): string {
  if (company.isHub && company.isMember) {
    return 'hub and member';
  }
  return company.isHub ? 'hub' : 'member';
}

function getNodeCenter(node: InternalNode): XYPosition {
  const { x, y } = node.internals.positionAbsolute;
  return { x: x + (node.measured.width ?? NODE_WIDTH) / 2, y: y + (node.measured.height ?? NODE_HEIGHT) / 2 };
}

function getBorderIntersection(node: InternalNode, otherNode: InternalNode): XYPosition {
  const center = getNodeCenter(node);
  const otherCenter = getNodeCenter(otherNode);
  const halfWidth = (node.measured.width ?? NODE_WIDTH) / 2;
  const halfHeight = (node.measured.height ?? NODE_HEIGHT) / 2;
  const dx = otherCenter.x - center.x;
  const dy = otherCenter.y - center.y;

  if (dx === 0 && dy === 0) {
    return center;
  }

  const scale = 1 / Math.max(Math.abs(dx) / halfWidth, Math.abs(dy) / halfHeight);
  return { x: center.x + dx * scale, y: center.y + dy * scale };
}

function layoutCompanies(companies: CompanyGroupCompany[], links: CompanyGroupLink[]): Map<string, XYPosition> {
  const hubIds = companies.filter((company) => company.isHub).map((company) => company.id);
  const hubIdSet = new Set(hubIds);
  const membersByHub = new Map<string, string[]>(hubIds.map((hubId) => [hubId, []]));
  const placedMembers = new Set<string>();

  for (const link of links) {
    if (hubIdSet.has(link.memberId) || placedMembers.has(link.memberId)) {
      continue;
    }
    placedMembers.add(link.memberId);
    membersByHub.get(link.hubId)?.push(link.memberId);
  }

  const positions = new Map<string, XYPosition>();
  let previousRadius: number | undefined;
  let hubX = 0;

  for (const hubId of hubIds) {
    const memberIds = membersByHub.get(hubId) ?? [];
    const radius = Math.max(MIN_RING_RADIUS, (memberIds.length * RING_NODE_SPACING) / (2 * Math.PI));

    if (previousRadius !== undefined) {
      hubX += previousRadius + radius + HUB_GAP;
    }
    previousRadius = radius;

    positions.set(hubId, toTopLeft({ x: hubX, y: 0 }));
    memberIds.forEach((memberId, index) => {
      const angle = (2 * Math.PI * index) / memberIds.length - Math.PI / 2;
      positions.set(memberId, toTopLeft({ x: hubX + radius * Math.cos(angle), y: radius * Math.sin(angle) }));
    });
  }

  return positions;
}

function toTopLeft({ x, y }: XYPosition): XYPosition {
  return { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 };
}
